import type { CoreAcessoRotinaUsuarioResponse } from "@r3backend/src/modules/Usuarios/app/DTO/coreAcessoRotina.dto";
import type { PagesMenu } from "~/types/pagesMenu";
import { menuStation as menuStationCatalogo } from "~/data/menuStation";
import { useAllowedMenus } from "~/store/menu/allowedMenus";
import { useUserStore } from "~/store/user/userStore";

/**
 * Monta o menu lateral desktop filtrado pelas permissões do usuário
 * (`CORE_ACESSO_USUARIO.ACEUSU_ID_MENU_PF`).
 *
 * O catálogo estático em `menuStation.ts` tem todos os itens com `visible: true`;
 * sem este filtro, rotinas sem acesso (ex.: 1150) apareciam no menu.
 *
 * Compara com:
 * - `item.nome` (id Delphi, ex. `mni_CadastroUsuario`)
 * - código no rótulo (`1150 — Cadastro…`)
 * - último segmento do `to` (`/gestaoAcessos/1150` → `1150`)
 *
 * "Início / Painel operacional" sempre fica liberado.
 */

function extrairCorpoAcessos(res: unknown): string[] {
	const r = res as {
		success?: boolean;
		response?: CoreAcessoRotinaUsuarioResponse | { ACEUSU_ID_MENU_PF?: string[] };
	} | CoreAcessoRotinaUsuarioResponse;

	if (r && typeof r === "object" && "response" in r && r.response) {
		const lista = (r.response as { ACEUSU_ID_MENU_PF?: string[] }).ACEUSU_ID_MENU_PF;
		return Array.isArray(lista) ? lista.map(String) : [];
	}
	if (r && typeof r === "object" && "ACEUSU_ID_MENU_PF" in r) {
		const lista = (r as CoreAcessoRotinaUsuarioResponse).ACEUSU_ID_MENU_PF;
		return Array.isArray(lista) ? lista.map(String) : [];
	}
	return [];
}

function codigoDoRotulo(label: string | undefined): string | null {
	if (!label) return null;
	// `1150 — Cadastro…`, `B321 - Averiguação…`, `34A — Status…`
	const m = label.match(/^([A-Za-z0-9]+)\s*[—–-]/);
	return m?.[1] ?? null;
}

function codigoDoPath(to: string | undefined): string | null {
	if (!to || to === "/") return null;
	const seg = to.split("/").filter(Boolean).pop();
	return seg ?? null;
}

function itemSempreLiberado(item: PagesMenu["items"] extends (infer I)[] | undefined ? I : never): boolean {
	const to = String((item as { to?: string })?.to ?? "");
	const nome = String((item as { nome?: string })?.nome ?? "");
	// Painéis de acompanhamento (TV) liberados no desktop enquanto não há ACL própria.
	if (to.startsWith("/acompanhamento/")) return true;
	if (nome.startsWith("mni_Acompanhamento")) return true;
	// 721 — Atribuição das mercadorias a não conformidade. Mesma situação dos
	// painéis acima: a rotina ainda não tem entrada em `CORE_ACESSO_USUARIO`
	// (conferido na base HPANORD: nenhuma linha com `721`), então o filtro
	// descartava o item e, com ele, o grupo inteiro. Liberada enquanto a
	// permissão não é cadastrada; a tela é somente consulta.
	if (to === "/processos/atribuicao-nao-conformidade") return true;
	return to === "/" || nome === "Home" || nome === "PainelOperacional";
}

function itemPermitido(
	item: NonNullable<PagesMenu["items"]>[number],
	permissoes: Set<string>,
): boolean {
	if (itemSempreLiberado(item)) return true;

	const nome = item.nome != null ? String(item.nome) : "";
	if (nome && permissoes.has(nome)) return true;

	const codLabel = codigoDoRotulo(item.label);
	if (codLabel && permissoes.has(codLabel)) return true;

	const codPath = codigoDoPath(item.to != null ? String(item.to) : undefined);
	if (codPath && permissoes.has(codPath)) return true;

	return false;
}

/**
 * Filtra o catálogo desktop pelas permissões do usuário logado.
 * Grava o resultado em `allowedMenus` (persistido).
 */
export default async function useSetMenusStation(): Promise<PagesMenu[]> {
	const allowedMenus = useAllowedMenus();
	const userStore = useUserStore();
	const { $r3fetchdata } = useNuxtApp();

	const usuario = Number(userStore.userID ?? 0);
	if (!usuario) {
		// Sem usuário: só o início.
		const soInicio = filtrarCatalogo(new Set());
		allowedMenus.setMenus(soInicio);
		return soInicio;
	}

	let permissoes = new Set<string>();
	try {
		const res = await $r3fetchdata.get("/api/usuarios/acessorotinas", {
			params: { USUARIO: usuario },
		});
		const lista = extrairCorpoAcessos(res);
		permissoes = new Set(lista.map(p => String(p).trim()).filter(Boolean));
		// Também guarda no userStore para checagens pontuais (useAcessoUsuario).
		userStore.setRotinasUsuario(lista as any);
	}
	catch (erro) {
		console.error("[menus/station] falha ao carregar permissões do usuário:", erro);
		// Fail-closed: não mostrar rotinas se não conseguimos validar.
		const soInicio = filtrarCatalogo(new Set());
		allowedMenus.setMenus(soInicio);
		throw erro;
	}

	const filtrado = filtrarCatalogo(permissoes);
	allowedMenus.setMenus(filtrado);
	return filtrado;
}

function filtrarCatalogo(permissoes: Set<string>): PagesMenu[] {
	const resultado: PagesMenu[] = [];

	for (const grupo of menuStationCatalogo) {
		const itensOrigem = grupo.items ?? [];
		const itens = itensOrigem
			.filter(item => itemPermitido(item, permissoes))
			.map(item => ({
				...item,
				visible: true,
			}));

		if (itens.length === 0) continue;

		resultado.push({
			...grupo,
			items: itens,
		});
	}

	// Garante ao menos o início se o catálogo mudou.
	if (resultado.length === 0) {
		resultado.push({
			label: "Início",
			icon: "pi pi-fw pi-home",
			items: [
				{
					label: "Painel operacional",
					icon: "pi pi-fw pi-home",
					to: "/",
					nome: "Home" as any,
					visible: true,
				},
			],
		});
	}

	return resultado;
}
